import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import {
  Search, ArrowUpDown, Download, ChevronDown, ChevronUp,
  UserPlus, EyeOff, ExternalLink, CheckCircle, Loader2,
  AlertTriangle, Zap, CircleDot,
} from 'lucide-react';
import api from '@/services/api';
import { toast } from 'sonner';
import InfoTip from '@/components/InfoTip';
import QuickAssignModal from '@/components/QuickAssignModal';
import { useNavigate } from 'react-router-dom';

const DataQualityBadge = ({ quality }) => {
  if (quality === 'verified') return <Badge className="bg-green-100 text-green-800 text-[10px]"><CircleDot className="h-2.5 w-2.5 mr-0.5" />Verified</Badge>;
  if (quality === 'estimated') return <Badge className="bg-amber-100 text-amber-800 text-[10px]"><CircleDot className="h-2.5 w-2.5 mr-0.5" />Estimated</Badge>;
  return <Badge className="bg-red-100 text-red-800 text-[10px]"><CircleDot className="h-2.5 w-2.5 mr-0.5" />N/A</Badge>;
};

const PriceDisplay = ({ price, quality }) => {
  if (!price || price <= 0 || quality === 'unavailable')
    return <span className="text-red-500 text-xs italic">Request Quote</span>;
  return <span>${price.toFixed(2)}</span>;
};

const BOMSmartAlternates = ({ bomId, bomName }) => {
  const navigate = useNavigate();
  const [alternates, setAlternates] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('value_impact');
  const [expandedRow, setExpandedRow] = useState(null);
  const [altDetails, setAltDetails] = useState({});
  const [altLoading, setAltLoading] = useState(null);

  // Approve
  const [approveTarget, setApproveTarget] = useState(null);
  const [switchingCost, setSwitchingCost] = useState('0');
  const [approving, setApproving] = useState(false);

  // Ignore
  const [ignoreOpen, setIgnoreOpen] = useState(false);
  const [ignoreTarget, setIgnoreTarget] = useState(null);
  const [ignoreReason, setIgnoreReason] = useState('');

  // Bulk selection
  const [selected, setSelected] = useState(new Set());
  const [bulkApproving, setBulkApproving] = useState(false);

  // Assign
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignCtx, setAssignCtx] = useState({});

  const fetchAlternates = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort_by: sortBy, bom_id: bomId });
      const res = await api.get(`/alternates/smart?${params}`);
      setAlternates(res.data.alternates || []);
      setSummary(res.data.summary || {});
    } catch { toast.error('Failed to load alternates'); }
    finally { setLoading(false); }
  }, [sortBy, bomId]);

  useEffect(() => { fetchAlternates(); }, [fetchAlternates]);

  const fetchComponentAlternates = async (componentId) => {
    if (altDetails[componentId]) {
      setExpandedRow(expandedRow === componentId ? null : componentId);
      return;
    }
    setAltLoading(componentId);
    try {
      const res = await api.get(`/alternates/component/${componentId}`);
      setAltDetails(prev => ({ ...prev, [componentId]: res.data }));
      setExpandedRow(componentId);
    } catch { toast.error('Failed to load detail'); }
    finally { setAltLoading(null); }
  };

  const handleApprove = async () => {
    if (!approveTarget) return;
    setApproving(true);
    try {
      const res = await api.post('/alternates/approve', {
        component_id: approveTarget.component_id,
        alternate_mpn: approveTarget.best_alternate_mpn,
        original_mpn: approveTarget.mpn,
        bom_id: approveTarget.bom_id,
        savings_value: approveTarget.savings_value,
        switching_cost: parseFloat(switchingCost) || 0,
      });
      toast.success(res.data.message);
      setApproveTarget(null);
      setSwitchingCost('0');
      fetchAlternates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approve failed'); }
    finally { setApproving(false); }
  };

  const handleBulkApprove = async () => {
    if (selected.size === 0) return;
    setBulkApproving(true);
    try {
      const items = alternates.filter(a => selected.has(a.component_id)).map(a => ({
        component_id: a.component_id, original_mpn: a.mpn,
        alternate_mpn: a.best_alternate_mpn, bom_id: a.bom_id,
        savings_value: a.savings_value, switching_cost: a.switching_cost || 0,
      }));
      const res = await api.post('/alternates/bulk-approve', { items });
      toast.success(res.data.message);
      setSelected(new Set());
      fetchAlternates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Bulk approve failed'); }
    finally { setBulkApproving(false); }
  };

  const handleIgnore = async () => {
    if (!ignoreTarget) return;
    try {
      await api.post('/alternates/ignore', {
        component_id: ignoreTarget.component_id,
        alternate_mpn: ignoreTarget.best_alternate_mpn,
        reason: ignoreReason,
      });
      toast.success('Alternate ignored');
      setIgnoreOpen(false);
      setIgnoreReason('');
      fetchAlternates();
    } catch { toast.error('Failed to ignore'); }
  };

  const handleExport = () => {
    const rows = filtered.map(a => [
      a.mpn, a.category, a.bom_name, a.best_alternate_mpn,
      a.current_price, a.best_alternate_price, a.savings_percent,
      a.savings_value, a.savings_type, a.switching_cost, a.net_savings,
      a.data_quality, a.approval_status,
    ].join(','));
    const header = 'MPN,Category,BOM,Best Alt,Your Price,Alt Price,Savings%,Savings$,Type,Switching Cost,Net Savings,Data Quality,Status';
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `smart-alternates-${bomName || bomId}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(a => a.component_id)));
  };

  const filtered = alternates.filter(a => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return a.mpn?.toLowerCase().includes(q) || a.best_alternate_mpn?.toLowerCase().includes(q);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="bom-smart-alternates-loading">
        <Loader2 className="h-10 w-10 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="bom-smart-alternates">
      {/* Bifurcated Savings Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="bom-alt-summary">
        <Card className="bg-blue-50 border-blue-200 shadow-sm">
          <CardContent className="pt-3 pb-2">
            <p className="text-[10px] text-blue-600 flex items-center gap-1">Alternates Found <InfoTip text="Total alternates found for components in this BOM." /></p>
            <p className="text-xl font-bold text-blue-700">{summary.total || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200 shadow-sm">
          <CardContent className="pt-3 pb-2">
            <p className="text-[10px] text-green-600 flex items-center gap-1">Total Savings <InfoTip text="Gross savings before switching costs." /></p>
            <p className="text-xl font-bold text-green-700">${(summary.total_savings || 0).toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50 border-emerald-200 shadow-sm">
          <CardContent className="pt-3 pb-2">
            <p className="text-[10px] text-emerald-600 flex items-center gap-1">Avg Savings <InfoTip text="Weighted median savings %. Prevents outlier distortion." /></p>
            <p className="text-xl font-bold text-emerald-700">{summary.weighted_median_savings || 0}%</p>
            <p className="text-[9px] text-gray-400">weighted median</p>
          </CardContent>
        </Card>
        <Card className="bg-cyan-50 border-cyan-200 shadow-sm">
          <CardContent className="pt-3 pb-2">
            <p className="text-[10px] text-cyan-600 flex items-center gap-1">Same-Make <InfoTip text="Savings from better pricing on the SAME MPN from different distributors." /></p>
            <p className="text-xl font-bold text-cyan-700">${(summary.same_make_savings || 0).toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="bg-purple-50 border-purple-200 shadow-sm">
          <CardContent className="pt-3 pb-2">
            <p className="text-[10px] text-purple-600 flex items-center gap-1">FFF Savings <InfoTip text="Savings from switching to a different MPN (form-fit-function alternate)." /></p>
            <p className="text-xl font-bold text-purple-700">${(summary.fff_savings || 0).toLocaleString()}</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200 shadow-sm">
          <CardContent className="pt-3 pb-2">
            <p className="text-[10px] text-amber-600 flex items-center gap-1">Net Savings <InfoTip text="Total savings minus estimated switching costs." /></p>
            <p className="text-xl font-bold text-amber-700">${(summary.total_net_savings || 0).toLocaleString()}</p>
            <p className="text-[9px] text-red-400">-${(summary.total_switching_cost || 0).toLocaleString()} switching</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters & Bulk Actions */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardContent className="pt-3 pb-3">
          <div className="flex gap-3 flex-wrap items-center">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <Input placeholder="Search MPN or alternate..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 h-9" data-testid="bom-alt-search" />
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-36 h-9" data-testid="bom-alt-sort"><ArrowUpDown className="h-3 w-3 mr-1" /><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="value_impact">Value Impact</SelectItem>
                <SelectItem value="savings_percent">Savings %</SelectItem>
                <SelectItem value="match_score">Match Score</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" className="h-9 gap-1" onClick={handleExport} data-testid="bom-alt-export-csv">
              <Download className="h-4 w-4" /> CSV
            </Button>
            <Button variant="outline" className="h-9 gap-1 text-green-700 border-green-300" onClick={() => navigate('/alternates')} data-testid="bom-alt-full-view">
              <ExternalLink className="h-4 w-4" /> Full View
            </Button>
            {selected.size > 0 && (
              <Button className="h-9 bg-green-600 hover:bg-green-700 text-white gap-1" onClick={handleBulkApprove} disabled={bulkApproving} data-testid="bom-alt-bulk-approve">
                {bulkApproving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
                Approve {selected.size}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {filtered.length === 0 ? (
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="py-12 text-center">
            <p className="text-gray-500" data-testid="bom-alt-empty">No smart alternates found for this BOM. Upload components or optimize the BOM first.</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-white border-gray-200 shadow-md">
          <CardContent className="pt-4">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="w-10">
                      <Checkbox checked={selected.size === filtered.length && filtered.length > 0} onCheckedChange={toggleSelectAll} data-testid="bom-alt-select-all" />
                    </TableHead>
                    <TableHead>MPN</TableHead>
                    <TableHead>Best Alternate</TableHead>
                    <TableHead>Your Price</TableHead>
                    <TableHead><span className="flex items-center gap-0.5">Savings <InfoTip text="Savings % = (Your Price - Alt Price) / Your Price x 100" /></span></TableHead>
                    <TableHead><span className="flex items-center gap-0.5">Type <InfoTip text="same_make = better price on same MPN. fff = different MPN." /></span></TableHead>
                    <TableHead><span className="flex items-center gap-0.5">Quality <InfoTip text="Verified = confirmed price. Estimated = spot price. N/A = no data." /></span></TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Detail</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((alt, idx) => (
                    <React.Fragment key={idx}>
                      <TableRow className={`hover:bg-gray-50 ${alt.approval_status === 'approved' ? 'bg-green-50/30' : ''}`} data-testid={`bom-alt-row-${idx}`}>
                        <TableCell>
                          <Checkbox checked={selected.has(alt.component_id)} onCheckedChange={() => toggleSelect(alt.component_id)} />
                        </TableCell>
                        <TableCell>
                          <button onClick={() => navigate(`/component-intelligence/${encodeURIComponent(alt.mpn)}`)} className="font-mono text-sm font-medium text-gray-900 hover:text-green-700">
                            {alt.mpn}
                          </button>
                          <p className="text-[10px] text-gray-400">{alt.category}</p>
                        </TableCell>
                        <TableCell className="font-mono text-sm font-medium text-green-700">{alt.best_alternate_mpn}</TableCell>
                        <TableCell className="text-sm">
                          {alt.current_price > 0 ? `$${alt.current_price.toFixed(2)}` : <span className="text-red-500 text-xs italic">N/A</span>}
                        </TableCell>
                        <TableCell>
                          <PriceDisplay price={alt.best_alternate_price} quality={alt.data_quality} />
                          {alt.savings_percent > 0 && <Badge className="bg-green-100 text-green-800 ml-1 text-[10px]">{alt.savings_percent}%</Badge>}
                        </TableCell>
                        <TableCell>
                          <Badge className={alt.savings_type === 'fff' ? 'bg-purple-100 text-purple-800 text-[10px]' : 'bg-cyan-100 text-cyan-800 text-[10px]'}>
                            {alt.savings_type === 'fff' ? 'FFF' : 'Same-Make'}
                          </Badge>
                        </TableCell>
                        <TableCell><DataQualityBadge quality={alt.data_quality} /></TableCell>
                        <TableCell>
                          {alt.approval_status === 'approved' ? (
                            <Badge className="bg-green-100 text-green-800 text-[10px]"><CheckCircle className="h-2.5 w-2.5 mr-0.5" />Approved</Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px]">Pending</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="outline" className={`h-7 px-2 text-xs ${expandedRow === alt.component_id ? 'bg-blue-50 border-blue-300' : ''}`}
                            onClick={() => fetchComponentAlternates(alt.component_id)} data-testid={`bom-alt-detail-${idx}`}>
                            {altLoading === alt.component_id ? '...' : expandedRow === alt.component_id ? <><ChevronUp className="h-3 w-3 mr-1" />Hide</> : <><ExternalLink className="h-3 w-3 mr-1" />Detail</>}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {alt.approval_status !== 'approved' && (
                              <Button size="sm" className="h-7 px-2 text-xs bg-green-600 hover:bg-green-700 text-white" onClick={() => setApproveTarget(alt)} data-testid={`bom-alt-approve-${idx}`}>
                                <Zap className="h-3 w-3 mr-0.5" /> Approve
                              </Button>
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="sm" variant="outline" className="h-7 px-1.5"><ChevronDown className="h-3 w-3" /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-44">
                                <DropdownMenuItem onClick={() => { setAssignCtx({ mpn: alt.mpn, sourceModule: 'Smart Alternates' }); setAssignOpen(true); }}>
                                  <UserPlus className="h-3.5 w-3.5 mr-2 text-blue-600" /> Assign
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => { setIgnoreTarget(alt); setIgnoreOpen(true); }}>
                                  <EyeOff className="h-3.5 w-3.5 mr-2 text-gray-500" /> Skip
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </TableCell>
                      </TableRow>
                      {/* Expanded Detail */}
                      {expandedRow === alt.component_id && altDetails[alt.component_id] && (
                        <TableRow data-testid={`bom-alt-expanded-${idx}`}>
                          <TableCell colSpan={10} className="bg-blue-50 border-l-4 border-l-blue-400 p-4">
                            <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                              Alternates for {alt.mpn}
                              <Badge className="bg-blue-100 text-blue-800 text-xs">{altDetails[alt.component_id].fff_alternates?.length || 0} FFF</Badge>
                              <Badge variant="outline" className="text-xs">{altDetails[alt.component_id].other_alternates?.length || 0} Other</Badge>
                            </h4>
                            <Table>
                              <TableHeader>
                                <TableRow className="bg-white/80">
                                  <TableHead>Alt MPN</TableHead>
                                  <TableHead>Type</TableHead>
                                  <TableHead>Manufacturer</TableHead>
                                  <TableHead>Price</TableHead>
                                  <TableHead>Quality</TableHead>
                                  <TableHead>Best Rate</TableHead>
                                  <TableHead>Risk</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {[...(altDetails[alt.component_id].fff_alternates || []), ...(altDetails[alt.component_id].other_alternates || [])].slice(0, 6).map((a, ai) => (
                                  <TableRow key={ai} className={a.is_fff ? 'bg-green-50/50' : ''}>
                                    <TableCell className="font-mono text-sm font-medium">{a.mpn}</TableCell>
                                    <TableCell><Badge className={a.is_fff ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-700'}>{a.match_type}</Badge></TableCell>
                                    <TableCell className="text-sm">{a.manufacturer}</TableCell>
                                    <TableCell><PriceDisplay price={a.price} quality={a.data_quality} /></TableCell>
                                    <TableCell><DataQualityBadge quality={a.data_quality} /></TableCell>
                                    <TableCell className="font-semibold text-green-700"><PriceDisplay price={a.best_rate} quality={a.data_quality} /></TableCell>
                                    <TableCell>
                                      <Badge className={a.risk_level === 'Low' ? 'bg-green-100 text-green-800' : a.risk_level === 'Medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}>
                                        {a.risk_level || 'Low'}
                                      </Badge>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="mt-3 flex items-center justify-between text-sm text-gray-500">
              <span>Showing {filtered.length} of {alternates.length} alternates</span>
              <span className="text-[10px] text-gray-400">Savings formula: (Your Price - Alt Price) / Your Price x 100</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Approved Summary */}
      {alternates.filter(a => a.approval_status === 'approved').length > 0 && (
        <Card className="bg-green-50 border-green-200 shadow-sm" data-testid="bom-alt-approved-summary">
          <CardContent className="pt-4 pb-3">
            <h4 className="text-sm font-semibold text-green-800 mb-2 flex items-center gap-2">
              <CheckCircle className="h-4 w-4" /> Approved Replacements in this BOM
            </h4>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-xs text-green-600">Approved</p>
                <p className="text-lg font-bold text-green-700">{alternates.filter(a => a.approval_status === 'approved').length}</p>
              </div>
              <div>
                <p className="text-xs text-green-600">Realized Savings</p>
                <p className="text-lg font-bold text-green-700">
                  ${alternates.filter(a => a.approval_status === 'approved').reduce((s, a) => s + a.savings_value, 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </p>
              </div>
              <div>
                <p className="text-xs text-green-600">Net (after costs)</p>
                <p className="text-lg font-bold text-emerald-700">
                  ${alternates.filter(a => a.approval_status === 'approved').reduce((s, a) => s + a.net_savings, 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Approve Dialog */}
      <Dialog open={!!approveTarget} onOpenChange={() => setApproveTarget(null)}>
        <DialogContent className="max-w-md" data-testid="bom-alt-approve-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Zap className="h-5 w-5 text-green-600" /> Approve & Replace</DialogTitle></DialogHeader>
          {approveTarget && (
            <div className="space-y-4 py-2">
              <div className="p-3 bg-gray-50 rounded-lg border space-y-1">
                <div className="flex justify-between text-sm"><span className="text-gray-500">Original:</span><span className="font-mono font-semibold">{approveTarget.mpn}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">Replace with:</span><span className="font-mono font-semibold text-green-700">{approveTarget.best_alternate_mpn}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">BOM:</span><span>{bomName || bomId}</span></div>
                <div className="flex justify-between text-sm"><span className="text-gray-500">Gross Savings:</span><span className="font-bold text-green-600">${approveTarget.savings_value?.toFixed(2)}</span></div>
              </div>
              <div>
                <Label className="flex items-center gap-1">
                  Estimated Switching Cost ($)
                  <InfoTip text="Cost of qualification testing, tooling changes, etc." />
                </Label>
                <Input type="number" step="0.01" value={switchingCost} onChange={e => setSwitchingCost(e.target.value)} className="mt-1" data-testid="bom-alt-switching-cost" />
                <p className="text-xs text-gray-500 mt-1">Net Savings: <strong className="text-emerald-700">${(approveTarget.savings_value - (parseFloat(switchingCost) || 0)).toFixed(2)}</strong></p>
              </div>
              <div className="p-2 bg-blue-50 rounded border border-blue-200 text-xs text-blue-700">
                <AlertTriangle className="h-3.5 w-3.5 inline mr-1" /> This creates a BOM change request and an Action Center item for procurement.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveTarget(null)}>Cancel</Button>
            <Button onClick={handleApprove} disabled={approving} className="bg-green-600 hover:bg-green-700 text-white" data-testid="bom-alt-confirm-approve">
              {approving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Approving...</> : <><CheckCircle className="h-4 w-4 mr-1" /> Approve & Replace</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ignore Dialog */}
      <Dialog open={ignoreOpen} onOpenChange={setIgnoreOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><EyeOff className="h-5 w-5 text-gray-500" /> Skip Suggestion</DialogTitle></DialogHeader>
          {ignoreTarget && (
            <div className="space-y-4 py-2">
              <div className="bg-gray-50 rounded p-3 text-sm">
                <p><strong>Component:</strong> {ignoreTarget.mpn}</p>
                <p><strong>Alternate:</strong> {ignoreTarget.best_alternate_mpn}</p>
              </div>
              <div><Label>Reason (optional)</Label><Textarea value={ignoreReason} onChange={e => setIgnoreReason(e.target.value)} placeholder="e.g., Already evaluated..." className="mt-1" rows={3} data-testid="bom-alt-ignore-reason" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIgnoreOpen(false)}>Cancel</Button>
            <Button onClick={handleIgnore} className="bg-gray-700 hover:bg-gray-800 text-white" data-testid="bom-alt-confirm-ignore">Skip</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <QuickAssignModal open={assignOpen} onClose={() => setAssignOpen(false)} context={assignCtx} />
    </div>
  );
};

export default BOMSmartAlternates;
